import { useState, useEffect } from 'react';
import { useLiff } from '@misterlex223/cloudflare-line-liff-sdk/react';
import { useLiffConfig } from '../providers/LiffProvider';

// Timeout to assume not in LIFF if initialization takes too long (10 seconds)
const LIFF_INIT_TIMEOUT = 10000;

/**
 * useShouldHideForLiff Hook
 * 
 * 用途：判斷在渲染階段某個 UI 元素是否應該因為在 LIFF 環境中而隱藏
 * 
 * 適用情境：
 * - 條件渲染：{!shouldHide && <Component />}
 * - 在渲染時就需要決定是否顯示的情境
 * 
 * 不適用情境：
 * - 事件處理中的判斷（直接使用 liff.isInClient() 即可）
 * 
 * 行為說明：
 * - LIFF 初始化中：返回 false（預設顯示 UI）
 * - LIFF 初始化成功且在 LINE App 中：返回 true
 * - LIFF 初始化成功但在外部瀏覽器：返回 false
 * - LIFF 初始化失敗：返回 false
 * - 超時（10秒）：返回 false
 *
 * @returns true 表示應該隱藏（在 LIFF 中），false 表示不隱藏（不在 LIFF 中）
 */
export const useShouldHideForLiff = (): boolean => {
  const config = useLiffConfig();
  const [shouldHide, setShouldHide] = useState(false); // 預設顯示（不在 LIFF 中）
  const [hasChecked, setHasChecked] = useState(false);
  
  const liff = useLiff({
    ...config,
    onInitSuccess: () => {
      console.log('[useShouldHideForLiff] LIFF initialized successfully');
      // 初始化成功後檢查是否在 LINE App 中
      try {
        const inClient = liff.isInClient();
        console.log('[useShouldHideForLiff] isInClient():', inClient);
        setShouldHide(inClient);
      } catch {
        console.error('[useShouldHideForLiff] isInClient() failed');
        setShouldHide(false);
      } finally {
        setHasChecked(true);
      }
    },
    onInitError: (error) => {
      console.error('[useShouldHideForLiff] LIFF initialization failed:', error);
      // 初始化失敗，假設不在 LIFF 中
      setShouldHide(false);
      setHasChecked(true);
    },
  });

  useEffect(() => {
    // 設置超時保護（10秒）
    const timeoutId = setTimeout(() => {
      if (!hasChecked) {
        console.log('[useShouldHideForLiff] Timeout reached, assuming not in LIFF');
        setShouldHide(false);
        setHasChecked(true);
      }
    }, LIFF_INIT_TIMEOUT);

    return () => clearTimeout(timeoutId);
  }, [hasChecked]);

  // 在初始化期間返回 false（預設顯示）
  if (!hasChecked) {
    console.log('[useShouldHideForLiff] Still checking, returning false (show UI)');
    return false;
  }

  console.log('[useShouldHideForLiff] Final result:', shouldHide);
  return shouldHide;
};

/**
 * @deprecated 請使用 useShouldHideForLiff()，命名更明確
 * 保留此導出以維持向後兼容
 */
export const useLiffDetection = useShouldHideForLiff;
